import { LucideProps } from "lucide-react";
import * as React from "react";
import InputMask from "react-input-mask";

import { cn } from "@/lib/utils";


export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  label?: string;
  Icon?: React.ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
  >;
  mask?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "h-10 w-full text-foreground relative bg-card border-muted-foreground border rounded-2xl focus-within:border-primary flex px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-ring  disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";
export { Input };

export const TextInput = ({
  Icon,
  label,
  className,
  value,
  ...rest
}: InputProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = !!value;

  return (
    <div
      className={cn(
        "h-14 w-full relative bg-card border-muted-foreground border rounded-2xl focus-within:border-primary",
        className
      )}
    >
      <span
        className={cn("absolute left-4 items-center font-normal duration-300", {
          "left-12": !!Icon,
          "text-xs translate-y-0 top-1": hasValue || isFocused,
          "top-[50%] translate-y-[-50%]": !isFocused && !hasValue,
          "text-muted-foreground": !isFocused,
          "text-primary": isFocused,
        })}
      >
        {label}
      </span>
      {Icon && (
        <span className="absolute inset-y-0 left-4 flex items-center">
          <Icon
            className={cn(
              "text-muted-foreground focus-within:hidden duration-300",
              {
                "text-primary": isFocused,
              }
            )}
          />
        </span>
      )}
      <input
        className={cn(
          "h-full w-full py-2 px-4 bg-transparent pt-6 relative focus:outline-none text-foreground",
          { "pl-12": !!Icon }
        )}
        autoComplete="off"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        value={value}
        {...rest}
      />
    </div>
  );
};

export const TextInputMask = ({
  Icon,
  label,
  mask = "",
  className,
  value,
  ...rest
}: InputProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = !!value;

  return (
    <div
      className={cn(
        "h-14 w-full relative bg-card border-muted-foreground border rounded-2xl focus-within:border-primary",
        className
      )}
    >
      <span
        className={cn("absolute left-4 items-center font-normal duration-300", {
          "left-12": !!Icon,
          "text-xs translate-y-0 top-1": hasValue || isFocused,
          "top-[50%] translate-y-[-50%]": !isFocused && !hasValue,
          "text-muted-foreground": !isFocused,
          "text-primary": isFocused,
        })}
      >
        {label}
      </span>
      {Icon && (
        <span className="absolute inset-y-0 left-4 flex items-center">
          <Icon
            className={cn(
              "text-muted-foreground focus-within:hidden duration-300",
              {
                "text-primary": isFocused,
              }
            )}
          />
        </span>
      )}
      <InputMask
        className={cn(
          "h-full w-full py-2 px-4 bg-transparent pt-6 relative focus:outline-none text-foreground",
          { "pl-12": !!Icon }
        )}
        autoComplete="off"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        mask={mask}
        maskChar=""
        value={value}
        {...rest}
      >
        {(inputProps) => <input {...inputProps} />}
      </InputMask>
    </div>
  );
};
